/**
 * Shared utilities for extracting data from report table rows
 * Used by both main app (useTableActions) and popup (usePopupTableActions)
 */

/**
 * Get value from row using case-insensitive key matching
 */
export const getRowValue = (row: any, searchKey: string): any => {
  const keys = Object.keys(row);
  const foundKey = keys.find(
    (key) => key.toLowerCase() === searchKey.toLowerCase()
  );
  return foundKey ? row[foundKey] : undefined;
};

/**
 * Get first MMSI value from row - handles various column names like "MMSI", "MMSI 1", "To MMSI", etc.
 */
export const getFirstMMSI = (row: any): string | undefined => {
  const keys = Object.keys(row);
  // Priority order: exact 'mmsi', then 'mmsi 1', then any field containing 'mmsi'
  const mmsiKey = keys.find(k => k.toLowerCase() === 'mmsi') 
    || keys.find(k => k.toLowerCase() === 'mmsi 1')
    || keys.find(k => k.toLowerCase() === 'to mmsi')
    || keys.find(k => k.toLowerCase().includes('mmsi'));
  return mmsiKey ? row[mmsiKey] : undefined;
};

/**
 * Check if row has ship-to-ship dual MMSI columns (e.g., "MMSI 1" and "MMSI 2")
 */
export const hasDualMMSI = (row: any): boolean => {
  const keys = Object.keys(row).map(k => k.toLowerCase());
  return (keys.includes('mmsi 1') && keys.includes('mmsi 2')) ||
         (keys.includes('mmsi a') && keys.includes('mmsi b'));
};

/**
 * Get MMSI for vessel A (first vessel in ship-to-ship reports)
 */
export const getMMSI_A = (row: any): string | undefined => {
  return getRowValue(row, 'mmsi 1') || getRowValue(row, 'mmsi a') || getRowValue(row, 'mmsi');
};

/**
 * Get MMSI for vessel B (second vessel in ship-to-ship reports)
 */
export const getMMSI_B = (row: any): string | undefined => {
  return getRowValue(row, 'mmsi 2') || getRowValue(row, 'mmsi b');
};

/**
 * Get Name for vessel A
 */
export const getName_A = (row: any): string | undefined => {
  return getRowValue(row, 'name 1') || getRowValue(row, 'name a') || getRowValue(row, 'name');
};

/**
 * Get Name for vessel B
 */
export const getName_B = (row: any): string | undefined => {
  return getRowValue(row, 'name 2') || getRowValue(row, 'name b');
};

/**
 * Normalize row data to have consistent lowercase property names
 * This helps with functions that expect lowercase properties like mmsi, imo, name
 */
export const normalizeRowForTarget = (row: any) => {
  const normalized = { ...row };

  // Add lowercase versions of common ship properties if they don't exist
  const fieldsToNormalize = ['mmsi', 'imo', 'name', 'callsign', 'to mmsi'];

  fieldsToNormalize.forEach((field) => {
    if (!normalized[field]) {
      const value = getRowValue(row, field);
      if (value !== undefined) {
        normalized[field] = value;
      }
    }
  });

  // Special handling for MMSI which may have various column names
  if (!normalized.mmsi) {
    const mmsiValue = getFirstMMSI(row);
    if (mmsiValue !== undefined) {
      normalized.mmsi = mmsiValue;
    }
  }

  return normalized;
};
